import { useRef } from 'react';
import { useAuthStore } from '../state/auth-store';
import { navigate } from '@/app/router';

/**
 * SPEC-002 §41 "Profile Navigation": profile must be reachable from the
 * App Shell user menu. Uses native <details>/<summary> for a zero-JS,
 * keyboard-accessible disclosure instead of adding a menu dependency for
 * two links (REQ-065 — no package without a simple native alternative).
 *
 * The <details> ref is used to explicitly close the menu after a
 * selection: this component stays mounted across route changes (only the
 * page content below the header swaps), so leaving `open` toggling purely
 * native would leave it open after selecting an item.
 */
export function UserMenu() {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const detailsRef = useRef<HTMLDetailsElement>(null);

  function closeMenu() {
    if (detailsRef.current) {
      detailsRef.current.open = false;
    }
  }

  function goToProfile() {
    closeMenu();
    navigate('/app/profile');
  }

  async function handleLogout() {
    closeMenu();
    await logout();
    navigate('/login');
  }

  const initials = (user?.displayName ?? '?')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <details ref={detailsRef} className="relative">
      <summary
        className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-full bg-accent text-sm font-semibold text-[#070807]"
        aria-label="Menu do usuário"
      >
        {initials}
      </summary>
      <div className="absolute right-0 z-10 mt-2 w-44 rounded-md border border-border bg-surface-2 p-1 shadow-md">
        <button
          type="button"
          onClick={goToProfile}
          className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text-primary hover:bg-surface"
        >
          Perfil
        </button>
        <button
          type="button"
          onClick={handleLogout}
          className="block w-full rounded-sm px-3 py-2 text-left text-sm text-text-primary hover:bg-surface"
        >
          Sair
        </button>
      </div>
    </details>
  );
}
